import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import { ORG_CONTACT, mailto } from "@/lib/orgContact";
import {
  Sparkles, Users, Calendar, Star, TrendingUp, ArrowRight,
  CheckCircle2, Clock, AlertCircle, XCircle, MessageSquare, ExternalLink,
} from "lucide-react";

const STATUS_META = {
  approved:   { label: "Profil approuvé", color: "border-emerald-300 bg-emerald-50 text-emerald-800", icon: CheckCircle2, tone: "positive" },
  pending:    { label: "En attente de validation", color: "border-amber-300 bg-amber-50 text-amber-800", icon: Clock, tone: "warning" },
  needs_info: { label: "Précisions demandées", color: "border-blue-300 bg-blue-50 text-blue-800", icon: AlertCircle, tone: "warning" },
  rejected:   { label: "Profil refusé", color: "border-red-300 bg-red-50 text-red-800", icon: XCircle, tone: "negative" },
};

const ROADMAP_STATUS = {
  live:    { label: "Disponible",   color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  planned: { label: "À venir",      color: "bg-navy-50 text-navy border-navy/25" },
};

/**
 * MentorDashboard — Landing page for approved network professionals.
 * Shows their vetting status, network profile summary, placeholder counters
 * for connection-requests/meetings/feedback (Phase 2), and a transparent
 * roadmap of upcoming features.
 */
export default function MentorDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/network/mentor/dashboard");
        setData(data);
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.info("Créez d'abord votre profil professionnel.");
          navigate("/join/professionnel");
        } else {
          toast.error("Impossible de charger votre espace mentor.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F3]">
        <TopBar />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <div className="altiora-tracing h-1 w-40 rounded-full" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { profile, status, status_note_fr, counters, roadmap } = data;
  const sMeta = STATUS_META[status] || STATUS_META.approved;
  const StatusIcon = sMeta.icon;
  const isApproved = status === "approved";

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="mentor-dashboard">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex items-start justify-between mb-10 gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Espace Mentor · {profile.role_network}</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">
              Bonjour {profile.display_name?.split(" ")[0] || ""}.
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#374151] leading-relaxed">
              Votre expertise fait désormais partie de l&apos;écosystème ALTIORA COOP.
              Voici l&apos;état de votre profil et les prochaines étapes.
            </p>
          </div>
        </div>

        {/* Status card — most important info first */}
        <div className={`border-2 ${sMeta.color} rounded-lg p-6 mb-10 flex items-start gap-4`} data-testid="mentor-status-card">
          <StatusIcon className="h-8 w-8 flex-shrink-0 mt-1" strokeWidth={1.8} />
          <div className="min-w-0 flex-1">
            <div className="font-display text-xl mb-1">{sMeta.label}</div>
            {status === "pending" && (
              <div className="text-sm leading-relaxed">
                Notre équipe examine votre profil. Vous recevrez un courriel dès qu&apos;une décision sera prise (généralement sous 24-48 h).
                D&apos;ici là, votre profil n&apos;apparaît pas encore dans les listings publics.
              </div>
            )}
            {status === "needs_info" && (
              <div className="text-sm leading-relaxed">
                <p>Notre équipe a besoin de précisions avant d&apos;approuver votre profil :</p>
                {status_note_fr && (
                  <blockquote className="mt-2 pl-3 border-l-2 border-current italic">
                    « {status_note_fr} »
                  </blockquote>
                )}
                <p className="mt-2">Répondez au courriel reçu ou contactez <a href={mailto("support")} className="underline font-medium" data-testid="mentor-support-link-1">{ORG_CONTACT.emails.support}</a>.</p>
              </div>
            )}
            {status === "rejected" && (
              <div className="text-sm leading-relaxed">
                <p>Après examen, votre profil n&apos;a pas été approuvé.</p>
                {status_note_fr && (
                  <blockquote className="mt-2 pl-3 border-l-2 border-current italic">
                    « {status_note_fr} »
                  </blockquote>
                )}
                <p className="mt-2">Pour toute question, écrivez à <a href={mailto("support")} className="underline font-medium" data-testid="mentor-support-link-2">{ORG_CONTACT.emails.support}</a>.</p>
              </div>
            )}
            {isApproved && (
              <div className="text-sm leading-relaxed">
                Votre profil est visible dans l&apos;écosystème et vous pouvez recevoir des demandes de mise en relation.
                {status_note_fr && (
                  <blockquote className="mt-2 pl-3 border-l-2 border-current italic">
                    « {status_note_fr} »
                  </blockquote>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Counters — placeholder for Phase 2 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <CounterCard
            icon={Users}
            label="Demandes reçues"
            value={counters.connection_requests_pending}
            sub={isApproved ? "En attente de votre réponse" : "Disponible après approbation"}
            enabled={isApproved}
            testId="counter-requests"
          />
          <CounterCard
            icon={Calendar}
            label="RDV à venir"
            value={counters.meetings_upcoming}
            sub="Prochaine session mentorat"
            enabled={isApproved}
            testId="counter-meetings"
          />
          <CounterCard
            icon={Star}
            label="Note moyenne"
            value={counters.feedback_average ?? "—"}
            sub={counters.feedback_average ? "Feedback bénéficiaires" : "Pas encore de retour"}
            enabled={isApproved}
            testId="counter-rating"
          />
          <CounterCard
            icon={TrendingUp}
            label="Vues du profil"
            value={counters.views_last_30d}
            sub="30 derniers jours"
            enabled={isApproved}
            testId="counter-views"
          />
        </div>

        {/* Profile summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 border-2 border-[#E5E7EB] bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-navy">Votre profil réseau</h2>
              <Link to="/app/network" className="text-xs font-mono uppercase tracking-widest text-navy hover:underline inline-flex items-center gap-1">
                Voir l&apos;écosystème <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <ProfileRow label="Intitulé">{profile.headline_fr}</ProfileRow>
            {profile.bio_fr && <ProfileRow label="Bio">{profile.bio_fr}</ProfileRow>}
            {profile.organization && <ProfileRow label="Organisation">{profile.organization}</ProfileRow>}
            {profile.function_title && <ProfileRow label="Fonction">{profile.function_title}</ProfileRow>}
            {profile.linkedin_url && (
              <ProfileRow label="LinkedIn">
                <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="text-navy hover:underline inline-flex items-center gap-1">
                  {profile.linkedin_url} <ExternalLink className="h-3 w-3" />
                </a>
              </ProfileRow>
            )}
            {(profile.expertises?.length || profile.sectors?.length) && (
              <ProfileRow label="Expertises · Secteurs">
                <div className="flex flex-wrap gap-1.5">
                  {(profile.expertises || []).map((e) => <span key={e} className="px-2 py-0.5 rounded border border-navy/25 bg-navy-50 text-navy text-xs">{e}</span>)}
                  {(profile.sectors || []).map((s) => <span key={s} className="px-2 py-0.5 rounded border border-[#E5E7EB] bg-[#F8F7F3] text-[#5B5B62] text-xs">{s}</span>)}
                </div>
              </ProfileRow>
            )}
            {profile.city && <ProfileRow label="Localisation">{profile.city}{profile.country ? ` · ${profile.country}` : ""}</ProfileRow>}
          </div>

          {/* Coming soon roadmap */}
          <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="mentor-roadmap">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-[#7A5B14]" />
              <h2 className="font-display text-lg text-navy">Feuille de route</h2>
            </div>
            <div className="space-y-3">
              {roadmap.map((item, i) => {
                const tone = ROADMAP_STATUS[item.status] || ROADMAP_STATUS.planned;
                return (
                  <div key={i} className="text-sm">
                    <div className="flex items-start gap-2">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border rounded-sm flex-shrink-0 ${tone.color}`}>
                        {tone.label}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">{item.phase}</span>
                    </div>
                    <div className="mt-1 text-[#374151] leading-snug">{item.feature}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="border border-dashed border-[#C5A880]/50 bg-[#FBF6EA]/60 rounded-lg p-6 flex items-start gap-3">
          <MessageSquare className="h-5 w-5 text-[#7A5B14] flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-sm text-[#7A5B14] leading-relaxed">
            <div className="font-medium mb-1">Une question, un souci, une idée ?</div>
            Notre équipe est joignable à <a href={mailto("support")} className="underline font-medium" data-testid="mentor-support-link-3">{ORG_CONTACT.emails.support}</a>.
            Vos retours façonnent la V2 de l&apos;espace mentor.
          </div>
        </div>
      </div>
    </div>
  );
}

function CounterCard({ icon: Icon, label, value, sub, enabled, testId }) {
  return (
    <div
      data-testid={testId}
      className={`border-2 rounded-lg p-4 ${enabled ? "border-[#E5E7EB] bg-white" : "border-[#F3F4F6] bg-[#FAFAF8] opacity-70"}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${enabled ? "text-navy" : "text-[#9CA3AF]"}`} strokeWidth={2} />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</span>
      </div>
      <div className={`font-display text-3xl ${enabled ? "text-navy" : "text-[#9CA3AF]"} leading-none`}>{value}</div>
      <div className="mt-2 text-[11px] text-[#8B8B8F] leading-snug">{sub}</div>
    </div>
  );
}

function ProfileRow({ label, children }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] mb-1">{label}</div>
      <div className="text-sm text-[#1F2937] leading-relaxed">{children}</div>
    </div>
  );
}
